/**
 * pintor.js — Dibuja una skin de 64×64 sobre un canvas.
 *
 * Las skins se GENERAN, no se descargan. Dos razones:
 *
 *  1. Es la convención del proyecto. Todo el atlas de bloques se pinta en
 *     canvas 2D (ver generateAtlasTexture en BlockType.js) y no hay un solo PNG
 *     de textura. Treinta PNG de skins romperían eso y pesarían más que el
 *     resto del juego junto.
 *
 *  2. Las skins de minecraftskins.com y laby.net las hizo gente, una a una.
 *     Sirven de INSPIRACIÓN para el catálogo de arquetipos —ninja, astronauta,
 *     buzo, esqueleto…—, que es lo que pidió Edu, pero copiarlas sería
 *     quedarse con el trabajo de otro.
 *
 * Cada skin es una paleta más una lista de adornos. El pintor sabe dibujar los
 * adornos; el catálogo solo los nombra.
 */
import { PARTES, CAPA2, CARAS } from './layout.js';

/* ── utilidades de píxel ────────────────────────────────────────────────── */

const R = (ctx, c, x, y, w, h) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };

/** Aclara u oscurece un color. Se usa para el sombreado de los costados: sin
 *  él todas las caras salen planas y el muñeco parece de cartón. */
export function tinte(hex, f) {
  const n = parseInt(hex.slice(1), 16);
  const m = (v) => Math.max(0, Math.min(255, Math.round(v * f)));
  return '#' + [m((n >> 16) & 255), m((n >> 8) & 255), m(n & 255)]
    .map((v) => v.toString(16).padStart(2, '0')).join('');
}

/** Pinta las seis caras de una parte, con la sombra propia de cada cara. */
function pintarParte(ctx, def, color, { sombra = true } = {}) {
  const f = sombra
    ? { frente: 1, espalda: 0.86, derecha: 0.92, izquierda: 0.92, arriba: 1.08, abajo: 0.72 }
    : { frente: 1, espalda: 1, derecha: 1, izquierda: 1, arriba: 1, abajo: 1 };
  for (const c of CARAS) {
    const [x, y, w, h] = def[c];
    R(ctx, tinte(color, f[c]), x, y, w, h);
  }
}

/** Ruido fino: dos de cada tres skins quedan muertas sin esto. Determinista,
 *  para que la misma skin salga siempre igual y los tests puedan mirarla. */
function granular(ctx, def, semilla, fuerza = 0.06) {
  let s = semilla;
  const azar = () => ((s = (s * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff);
  for (const c of CARAS) {
    const [x, y, w, h] = def[c];
    const d = ctx.getImageData(x, y, w, h);
    for (let i = 0; i < d.data.length; i += 4) {
      if (d.data[i + 3] === 0) continue;
      const k = 1 + (azar() - 0.5) * fuerza * 2;
      d.data[i] *= k; d.data[i + 1] *= k; d.data[i + 2] *= k;
    }
    ctx.putImageData(d, x, y);
  }
}

/* ── la cara ───────────────────────────────────────────────────────────── */

/** El frente de la cabeza. `estilo` cambia los ojos y la boca; todo lo demás
 *  —posición, tamaño— se mantiene, porque a 8×8 píxeles mover un ojo un píxel
 *  ya cambia de personaje. */
function pintarCara(ctx, estilo, col) {
  const [fx, fy] = PARTES.cabeza.frente;
  const p = (dx, dy, w, h, c) => R(ctx, c, fx + dx, fy + dy, w, h);

  if (estilo === 'visor') {                       // astronauta, buzo, robot
    p(0, 2, 8, 4, col.visor || '#1B2A44');
    p(1, 3, 2, 1, '#FFFFFF55');
    return;
  }
  if (estilo === 'monstruo') {                    // zombi, momia
    p(1, 3, 2, 2, '#000000'); p(5, 3, 2, 2, '#000000');
    p(2, 6, 4, 1, '#2A1A1A');
    return;
  }
  if (estilo === 'hueso') {                       // esqueleto
    p(1, 3, 2, 2, '#111111'); p(5, 3, 2, 2, '#111111');
    p(2, 6, 4, 1, '#111111');
    for (let i = 0; i < 3; i++) p(2 + i * 2, 6, 1, 1, col.piel);
    return;
  }
  if (estilo === 'creeper') {
    p(1, 2, 2, 2, '#000000'); p(5, 2, 2, 2, '#000000');
    p(3, 4, 2, 2, '#000000'); p(2, 5, 1, 2, '#000000'); p(5, 5, 1, 2, '#000000');
    return;
  }
  if (estilo === 'animal') {
    p(1, 3, 2, 2, '#FFFFFF'); p(5, 3, 2, 2, '#FFFFFF');
    p(2, 4, 1, 1, '#000000'); p(5, 4, 1, 1, '#000000');
    p(3, 5, 2, 2, col.hocico || '#2A2A2A');       // hocico
    p(2, 7, 4, 1, tinte(col.piel, 0.85));
    return;
  }
  /* humana */
  p(1, 3, 2, 2, '#FFFFFF'); p(5, 3, 2, 2, '#FFFFFF');
  p(2, 3, 1, 2, col.ojos || '#4A3A28'); p(5, 3, 1, 2, col.ojos || '#4A3A28');
  p(1, 2, 2, 1, tinte(col.pelo, 0.8)); p(5, 2, 2, 1, tinte(col.pelo, 0.8));   // cejas
  p(3, 5, 2, 1, tinte(col.piel, 0.88));                                        // nariz
  p(2, 6, 4, 1, tinte(col.piel, 0.7));                                         // boca
}

/* ── adornos ───────────────────────────────────────────────────────────── */

const ADORNOS = {
  /** Pelo en la capa 2: casquete arriba, atrás y patillas.
   *
   *  Los costados se recortan a la mitad de altura. Cubriéndolos enteros —que
   *  es lo que hacía antes— el pelo bajaba hasta la barbilla y todos los
   *  personajes salían con el mismo casco de paje. */
  pelo(ctx, col) {
    const c = CAPA2.cabeza;
    pintarParte(ctx, c, col.pelo);
    const [fx, fy] = c.frente;
    ctx.clearRect(fx, fy + 2, 8, 6);              // la cara, con flequillo
    for (const lado of ['derecha', 'izquierda']) {
      const [x, y] = c[lado];
      ctx.clearRect(x, y + 4, 8, 4);              // patilla corta
    }
  },
  /** La capucha sí baja por los lados: es tela, no pelo. Deja ver la cara por
   *  un óvalo, que es lo que la distingue de un casco. */
  capucha(ctx, col) {
    const c = CAPA2.cabeza;
    pintarParte(ctx, c, col.detalle || col.camisa);
    const [fx, fy] = c.frente;
    ctx.clearRect(fx + 1, fy + 2, 6, 5);
  },
  casco(ctx, col) {
    const c = CAPA2.cabeza;
    pintarParte(ctx, c, col.detalle || '#B8B8C0');
    const [fx, fy] = c.frente;
    ctx.clearRect(fx + 1, fy + 3, 6, 3);
  },
  /** Corona: un aro con puntas, no un casco. Con «casco» el rey y la reina
   *  salían con una capucha amarilla y no se entendía qué eran. */
  corona(ctx, col) {
    const c = CAPA2.cabeza;
    const oro = col.detalle || '#FFD700';
    for (const lado of CARAS) {
      if (lado === 'arriba' || lado === 'abajo') continue;
      const [x, y, w] = c[lado];
      R(ctx, oro, x, y + 1, w, 2);                       // el aro
      for (let i = 0; i < w; i += 2) R(ctx, oro, x + i, y, 1, 1);   // las puntas
    }
  },
  orejas(ctx, col) {
    const c = CAPA2.cabeza;
    const [ax, ay] = c.arriba;
    R(ctx, col.detalle || col.pelo, ax, ay, 2, 2);
    R(ctx, col.detalle || col.pelo, ax + 6, ay, 2, 2);
  },
  cinturon(ctx, col) {
    const [x, y] = PARTES.torso.frente;
    R(ctx, col.detalle || '#3A2A1A', x, y + 8, 8, 2);
    R(ctx, '#D4AF37', x + 3, y + 8, 2, 2);
    const [bx, by] = PARTES.torso.espalda;
    R(ctx, col.detalle || '#3A2A1A', bx, by + 8, 8, 2);
  },
  corbata(ctx, col) {
    const [x, y] = PARTES.torso.frente;
    R(ctx, col.detalle || '#8B1A1A', x + 3, y, 2, 7);
  },
  rayas(ctx, col) {
    for (const parte of [PARTES.torso, PARTES.brazoDerecho, PARTES.brazoIzquierdo])
      for (const c of CARAS) {
        const [x, y, w, h] = parte[c];
        for (let j = 1; j < h; j += 3) R(ctx, col.detalle || '#FFFFFF', x, y + j, w, 1);
      }
  },
  peto(ctx, col) {
    const [x, y] = PARTES.torso.frente;
    R(ctx, col.detalle || '#C8CDD4', x + 1, y + 1, 6, 8);
    R(ctx, tinte(col.detalle || '#C8CDD4', 0.8), x + 1, y + 4, 6, 1);
  },
  emblema(ctx, col) {
    const [x, y] = PARTES.torso.frente;
    R(ctx, col.detalle || '#D4AF37', x + 3, y + 3, 2, 2);
  },
  mochila(ctx, col) {
    const c = CAPA2.torso;
    const [x, y] = c.espalda;
    R(ctx, col.detalle || '#4A3A28', x + 1, y + 1, 6, 8);
  },
};

/* ── el pintor ─────────────────────────────────────────────────────────── */

/**
 * Pinta una skin completa sobre un canvas de 64×64.
 * @param {CanvasRenderingContext2D} ctx
 * @param {object} skin  entrada del catálogo
 */
export function pintarSkin(ctx, skin) {
  ctx.clearRect(0, 0, 64, 64);
  ctx.imageSmoothingEnabled = false;
  const col = skin;

  // piel: cabeza y manos
  pintarParte(ctx, PARTES.cabeza, col.piel);
  pintarParte(ctx, PARTES.brazoDerecho, col.camisa);
  pintarParte(ctx, PARTES.brazoIzquierdo, col.camisa);
  // las manos, en el cuarto inferior del brazo
  for (const b of [PARTES.brazoDerecho, PARTES.brazoIzquierdo])
    for (const c of ['frente', 'espalda', 'derecha', 'izquierda']) {
      const [x, y, w, h] = b[c];
      R(ctx, col.piel, x, y + h - 3, w, 3);
    }

  pintarParte(ctx, PARTES.torso, col.camisa);
  pintarParte(ctx, PARTES.piernaDerecha, col.pantalon);
  pintarParte(ctx, PARTES.piernaIzquierda, col.pantalon);
  // zapatos
  for (const p of [PARTES.piernaDerecha, PARTES.piernaIzquierda]) {
    for (const c of ['frente', 'espalda', 'derecha', 'izquierda']) {
      const [x, y, w, h] = p[c];
      R(ctx, col.zapatos, x, y + h - 3, w, 3);
    }
    const [bx, by, bw, bh] = p.abajo;
    R(ctx, col.zapatos, bx, by, bw, bh);
  }

  pintarCara(ctx, skin.cara || 'humana', col);
  for (const a of skin.adornos || []) ADORNOS[a]?.(ctx, col);

  // el grano va al final, sobre todo lo pintado
  let semilla = 0;
  for (let i = 0; i < skin.id.length; i++) semilla = (semilla * 31 + skin.id.charCodeAt(i)) & 0x7fffffff;
  for (const p of Object.values(PARTES)) granular(ctx, p, semilla + 7, 0.07);
}

/** Devuelve un canvas de 64×64 ya pintado. */
export function lienzoDeSkin(skin) {
  const c = document.createElement('canvas');
  c.width = 64; c.height = 64;
  pintarSkin(c.getContext('2d'), skin);
  return c;
}

/* ── retrato plano ─────────────────────────────────────────────────────── */

/**
 * Monta el muñeco de frente (o de espaldas) en 2D, recortando de la skin las
 * caras que se verían. Es el mismo dibujo que enseñan las webs de skins en su
 * ficha, y cuesta un canvas de 16×32 en vez de un contexto WebGL: con cuarenta
 * skins en una rejilla, cuarenta lienzos 3D dejarían el menú inservible.
 */
export function retrato(skin, escala = 6, dorso = false) {
  const src = lienzoDeSkin(skin);
  const cara = dorso ? 'espalda' : 'frente';
  const out = document.createElement('canvas');
  out.width = 16 * escala; out.height = 32 * escala;
  const ctx = out.getContext('2d');
  ctx.imageSmoothingEnabled = false;

  // [parte, x, y] en la retícula de 16×32; de espaldas se refleja el eje X
  const piezas = [
    ['cabeza', 4, 0, 8, 8],
    ['torso', 4, 8, 8, 12],
    ['brazoDerecho', dorso ? 12 : 0, 8, 4, 12],
    ['brazoIzquierdo', dorso ? 0 : 12, 8, 4, 12],
    ['piernaDerecha', dorso ? 8 : 4, 20, 4, 12],
    ['piernaIzquierda', dorso ? 4 : 8, 20, 4, 12],
  ];
  for (const capa of [PARTES, CAPA2])
    for (const [parte, dx, dy, w, h] of piezas) {
      const [sx, sy, sw, sh] = capa[parte][cara];
      ctx.drawImage(src, sx, sy, sw, sh, dx * escala, dy * escala, w * escala, h * escala);
    }
  return out;
}
